<div class="modal-overlay">
  <div class="modal-box">
    <!-- Floating Edge Tab for Release Notes -->
    <button type="button" class="drawer-edge-tab" (click)="toggleReleaseNotesDrawer()"
      [class.active]="showReleaseNoteDrawer" [title]="showReleaseNoteDrawer ? 'Close Release Notes' : 'Open Release Notes'">
      <span class="tab-icon">📝</span>
      <span class="tab-label">Release Notes</span>
      <span class="tab-arrow">{{ showReleaseNoteDrawer ? '◀' : '▶' }}</span>
    </button>

    <!-- Modal Header -->
    <div class="modal-header">
      <div class="modal-title-group">
        <h3 class="modal-title">Release App to Device</h3>
        <p class="modal-subtitle">{{ deviceName }} ({{ deviceId }})</p>
      </div>
      <div class="header-action-group">
        <button type="button" class="btn-header-notes" (click)="toggleReleaseNotesDrawer(true)"
          title="View Release Notes for selected version">
          📝 Release Notes
        </button>
        <button class="close-btn" (click)="close.emit()">×</button>
      </div>
    </div>

    <div class="modal-body">
      <!-- Loading State -->
      <div *ngIf="isLoading" class="loading-container">
        <div class="spinner"></div>
      </div>

      <div *ngIf="!isLoading">
        <!-- Device Current Status Banner -->
        <div class="status-banner">
          <div class="status-item">
            <span class="status-item-label">Currently Installed:</span>
            <span class="status-item-value version-badge-current">
              {{ currentAppVersion ? 'v' + currentAppVersion : 'Unknown' }}
            </span>
          </div>

          <div class="status-item" *ngIf="deviceTargetVersion">
            <span class="status-item-label">Targeted Version:</span>
            <span class="status-item-value version-badge-target">
              v{{ deviceTargetVersion }}
              <span *ngIf="deviceUpdateNeeded && deviceTargetVersion !== currentAppVersion" class="pending-pill">Update
                Pending</span>
              <span *ngIf="deviceTargetVersion === currentAppVersion || !deviceUpdateNeeded"
                class="installed-pill">Installed</span>
            </span>
          </div>

          <button class="btn-refresh-status" (click)="loadData()" title="Refresh device status">
            🔄 Refresh Status
          </button>
        </div>

        <!-- Release Selection Form -->
        <div class="form-section">
          <label class="form-label" for="versionSelect">Select App Release Version:</label>
          <select id="versionSelect" class="form-select" [value]="selectedVersion?.version"
            (change)="onSelectVersion($event)">
            <option *ngFor="let ver of appVersionsList" [value]="ver.version">
              v{{ ver.version }} (Published: {{ ver.publishedAt ? (ver.publishedAt | date:'medium') : 'N/A' }})
            </option>
          </select>
          <div *ngIf="appVersionsList.length === 0" class="empty-hint">
            No app releases uploaded yet. Upload a release ZIP first using the build CLI tool.
          </div>
        </div>

        <!-- Selected Version Details -->
        <div class="version-details-card" *ngIf="selectedVersion">
          <div class="detail-row">
            <span class="detail-label">Version Number:</span>
            <span class="detail-val font-bold">v{{ selectedVersion.version }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Published Date:</span>
            <span class="detail-val">{{ selectedVersion.publishedAt ? (selectedVersion.publishedAt | date:'medium') :
              'N/A' }}</span>
          </div>
          <div class="detail-row" *ngIf="selectedVersion.publishedBy">
            <span class="detail-label">Published By:</span>
            <span class="detail-val">{{ selectedVersion.publishedBy }}</span>
          </div>

          <div class="version-notes-shortcut">
            <button type="button" class="btn-shortcut-notes" (click)="toggleReleaseNotesDrawer(true)">
              <span class="shortcut-icon">📝</span>
              <span class="shortcut-text">View / Edit Release Notes for v{{ selectedVersion.version }}</span>
              <span class="shortcut-arrow">➔</span>
            </button>
          </div>
        </div>

        <!-- Restart Option Checkbox -->
        <div class="checkbox-container">
          <label class="checkbox-label">
            <input type="checkbox" class="custom-checkbox" [(ngModel)]="triggerRestart" />
            Restart application immediately after publishing
          </label>
          <p class="checkbox-hint">
            If checked, a restart command is sent to the kiosk so it immediately downloads and applies the update. If
            unchecked, the kiosk will apply it upon its next scheduled restart.
          </p>
        </div>

        <!-- Force Update Checkbox -->
        <div class="checkbox-container">
          <label class="checkbox-label warning-label">
            <input type="checkbox" class="custom-checkbox" [(ngModel)]="forceUpdate" />
            Force Reinstall / Override Version Check & Settings
          </label>
          <p class="checkbox-hint">
            Forces the machine to re-download and re-install this package even if it is already running this version,
            and overrides local "Disable Auto Updates" settings.
          </p>
        </div>

        <!-- Messages -->
        <div *ngIf="statusMessage" class="alert-success">
          {{ statusMessage }}
        </div>
        <div *ngIf="errorMessage" class="alert-error">
          {{ errorMessage }}
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button class="btn-cancel" (click)="close.emit()" [disabled]="isPublishing">Cancel</button>
      <button class="btn-publish" (click)="publishToDevice()"
        [disabled]="isPublishing || !selectedVersion || isLoading">
        <span *ngIf="!isPublishing">🚀 Release to This Device</span>
        <span *ngIf="isPublishing">
          <span class="spinner-small"></span>
          Publishing...
        </span>
      </button>
    </div>

    <!-- Slide-in Release Notes Drawer (Slides from Left to Right) -->
    <div class="release-note-drawer" [class.open]="showReleaseNoteDrawer">
      <div class="drawer-header">
        <div class="drawer-title-group">
          <h4 class="drawer-title">
            <span class="drawer-header-icon">📝</span>
            Release Notes — v{{ selectedVersion?.version }}
          </h4>
          <p class="drawer-subtitle" *ngIf="releaseNoteUpdatedAt">
            Updated {{ releaseNoteUpdatedAt | date:'medium' }}
            <span *ngIf="releaseNoteUpdatedBy">by {{ releaseNoteUpdatedBy }}</span>
          </p>
        </div>

        <div class="drawer-header-actions">
          <button type="button" class="btn-drawer-edit" *ngIf="!isEditingNote && !isLoadingNote"
            (click)="startEditNote()" title="Edit Release Note">
            ✏️ Edit
          </button>
          <button type="button" class="btn-drawer-delete" *ngIf="!isEditingNote && !isLoadingNote && releaseNoteContent"
            (click)="deleteReleaseNote()" title="Delete Release Note">
            🗑️ Delete
          </button>
          <button type="button" class="drawer-close-btn" (click)="toggleReleaseNotesDrawer(false)"
            title="Close Release Notes">✕</button>
        </div>
      </div>

      <div class="drawer-body">
        <!-- Drawer Alert Messages -->
        <div *ngIf="noteFeedbackMessage" class="alert-success drawer-alert">
          {{ noteFeedbackMessage }}
        </div>
        <div *ngIf="noteErrorMessage" class="alert-error drawer-alert">
          {{ noteErrorMessage }}
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoadingNote" class="drawer-loading-container">
          <div class="spinner"></div>
          <p class="drawer-loading-text">Loading release notes for v{{ selectedVersion?.version }}...</p>
        </div>

        <!-- View Mode -->
        <div *ngIf="!isLoadingNote && !isEditingNote" class="drawer-view-section">
          <div *ngIf="releaseNoteContent" class="note-view-wrapper ql-snow">
            <div class="ql-editor note-view-editor" [innerHTML]="getSanitizedNote()"></div>
          </div>

          <div *ngIf="!releaseNoteContent" class="empty-note-state">
            <div class="empty-note-icon">📋</div>
            <h5 class="empty-note-title">No Release Note Available</h5>
            <p class="empty-note-desc">
              No release notes have been added for Version {{ selectedVersion?.version }} yet.
            </p>
            <button type="button" class="btn-create-note" (click)="startEditNote()">
              ➕ Add Release Note
            </button>
          </div>
        </div>

        <!-- Edit / Add Mode -->
        <div *ngIf="!isLoadingNote && isEditingNote" class="drawer-edit-section">
          <div class="editor-section-header">
            <span class="editor-section-label">Edit Formatted Release Notes:</span>
          </div>

          <div class="quill-editor-container">
            <quill-editor [(ngModel)]="releaseNoteContent" [modules]="quillConfig"
              placeholder="Type release highlights, changelogs, bug fixes, or instructions...">
            </quill-editor>
          </div>

          <div class="drawer-edit-footer">
            <button type="button" class="btn-cancel-edit" (click)="cancelEditNote()" [disabled]="isSavingNote">
              Cancel
            </button>
            <button type="button" class="btn-save-note" (click)="saveReleaseNote()" [disabled]="isSavingNote">
              <span *ngIf="!isSavingNote">💾 Save Release Note</span>
              <span *ngIf="isSavingNote">
                <span class="spinner-small"></span>
                Saving...
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>